/**
 * Pendo Analytics Service (Web only)
 *
 * Wraps window.pendo so all calls are type-safe and safely no-op when the
 * agent hasn't loaded yet (e.g. blocked by an ad-blocker or during SSR).
 *
 * The Pendo agent loader snippet in public/index.html bootstraps
 * `window.pendo` with a queued interface, so calls made before the async
 * agent script finishes loading are buffered and replayed automatically.
 *
 * App ID is read from PENDO_APP_ID in .env (injected into index.html at
 * build time via HtmlWebpackPlugin templateParameters).
 */

import type { UserDetails } from '../../features/auth/screens/userProps';

// ─── TypeScript global declaration ──────────────────────────────────────────

declare global {
  interface Window {
    pendo?: {
      initialize: (options: PendoInitOptions) => void;
      identify: (options: PendoInitOptions) => void;
      updateOptions: (options: Partial<PendoInitOptions>) => void;
      pageLoad: (url?: string) => void;
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      /** Internal queue used by the loader snippet before the agent loads. */
      _q: unknown[];
    };
  }
}

// ─── Internal types ──────────────────────────────────────────────────────────

interface PendoVisitor {
  /** Required — unique user identifier. */
  id: string;
  email?: string;
  full_name?: string;
}

interface PendoAccount {
  /** Required — unique agency identifier (agency host / X-Agency-Id value). */
  id: string;
  name?: string;
}

interface PendoInitOptions {
  visitor: PendoVisitor;
  account: PendoAccount;
}

/**
 * Minimal agency shape consumed by pendoService.
 * Callers may pass the full agency object from AppContext — only `host` is
 * required; `label` is used as the Pendo account display name when present.
 */
export interface PendoAgency {
  host: string;
  label?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isPendoReady(): boolean {
  return globalThis.window?.pendo !== undefined;
}

function buildOptions(user: UserDetails, agency: PendoAgency | null): PendoInitOptions {
  // The BFF returns `userID` (capital D); the UserDetails interface declares `userId`.
  // Support both to handle the casing mismatch without requiring an API change.
  const visitorId = (user as any).userID ?? user.userId;
  return {
    visitor: {
      id: String(visitorId ?? ''),
      email: user.email ?? undefined,
      full_name:
        [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
    },
    account: {
      id: agency?.host ?? 'unknown',
      name: agency?.label ?? undefined,
    },
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const pendoService = {
  /**
   * Call once after the user successfully authenticates.
   * Creates (or updates) the visitor and account session in Pendo.
   *
   * @param user    - Authenticated UserDetails object from Redux / AppContext.
   * @param agency  - Active agency (may be null on first load).
   */
  initialize(user: UserDetails, agency: PendoAgency | null): void {
    if (!isPendoReady()) {
      console.warn('[Pendo] Agent not available — skipping initialize');
      return;
    }
    const options = buildOptions(user, agency);
    if (!options.visitor.id) {
      console.warn('[Pendo] initialize() skipped — visitor.id is empty (userId not yet available)');
      return;
    }
    try {
      globalThis.window.pendo!.initialize(options);
    } catch (err) {
      console.error('[Pendo] initialize() threw:', err);
    }
  },

  /**
   * Re-identify the visitor without resetting the session.
   * Useful when the user or agency context changes mid-session.
   */
  identify(user: UserDetails, agency: PendoAgency | null): void {
    if (!isPendoReady()) return;
    try {
      globalThis.window.pendo!.identify(buildOptions(user, agency));
    } catch (err) {
      console.error('[Pendo] identify() threw:', err);
    }
  },

  /**
   * Trigger a manual page-load event.
   * Pendo auto-detects SPA route changes via the URL hash/history, but calling
   * this explicitly after navigation ensures reliable tracking in React Router.
   */
  pageLoad(url?: string): void {
    if (!isPendoReady()) return;
    try {
      globalThis.window.pendo!.pageLoad(url);
    } catch (err) {
      console.error('[Pendo] pageLoad() threw:', err);
    }
  },

  /**
   * Track a custom event.
   */
  track(eventName: string, metadata?: Record<string, unknown>): void {
    if (!isPendoReady()) return;
    try {
      globalThis.window.pendo!.track(eventName, metadata);
    } catch (err) {
      console.error('[Pendo] track() threw:', err);
    }
  },
};